/* 186 B.7a [RULED 186 (10)] — THE PAGER CONTROL: Previous · `common.page_of` ·
 * Next, the `.csPgr` markup the chronology and the Documents section each
 * rendered as their own copy, moved here once (the class stays in App.css).
 * The state (`usePager`) and the arithmetic (`pageOf`) behind it are
 * `hooks/usePager.ts`; a caller
 * renders this when it has more than one page, exactly as both copies did
 * (`tot > 1`, the Documents section's `paginate &&` in front of it).
 *
 * EXTRACT, DON'T IMPROVE: the two buttons, their disabled edges and the
 * three keys are the copies' own, character for character.
 */
import React from "react";
import type { Dispatch } from "react";
import { useTranslation } from "../i18n";
import type { Translate } from "../lib/interestRefusal";
import type { Page } from "../hooks/usePager";

/* 188 D.3 — `setPage` WIDENED to `Dispatch<number>` (React's `(value: number) =>
 * void`, an imported type — a named parameter written here would be a
 * `no-unused-vars` warning in a zero-warning file, 186's method). The four
 * first callers passed `usePager()`'s own setter, `Dispatch<SetStateAction<
 * number>>`; three of the five 188 converts (CaseAgendaSection, CaseTeamSection,
 * CaseTasksSection) receive their page setter from CaseView as a plain
 * `(v: number) => void` prop, which `strictFunctionTypes` refuses against the
 * state-setter type. This control only ever calls `setPage(n)` with a number,
 * so `Dispatch<number>` is the union both shapes satisfy: a state setter is
 * assignable to it, a plain setter is it. Type-only; nothing renders differently. */
type Props = Pick<Page<unknown>, "cur" | "tot"> & { setPage: Dispatch<number> };

export default function Pager({ cur, tot, setPage }: Readonly<Props>) {
  const { t } = useTranslation() as { t: Translate };
  return (
    <div className="csPgr">
      <button
        disabled={cur === 0}
        onClick={() => {
          setPage(cur - 1);
        }}
      >
        {t("common.previous", { fallback: "Previous" })}
      </button>
      <span>
        {t("common.page_of", {
          cur: cur + 1,
          total: tot,
          fallback: `Page ${String(cur + 1)} of ${String(tot)}`,
        })}
      </span>
      <button
        disabled={cur >= tot - 1}
        onClick={() => {
          setPage(cur + 1);
        }}
      >
        {t("common.next", { fallback: "Next" })}
      </button>
    </div>
  );
}
